import { Box, GridLegacy as Grid, Skeleton, Stack, Typography, useTheme } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { MailingAddress } from "components/Facility/MailingAddress";
import { PhysicalAddress } from "components/Facility/PhysicalAddress";
import { FireDepartmentPicker } from "components/FireDepartmentPicker";
import { FormTextField } from "components/Forms/FormTextField";
import { RadioGroupField } from "components/Forms/RadioGroupField";
import { SupplementalFireDepartmentSection } from "components/Forms/SupplementalFireDepartmentSection";
import { SupplementalLepcSection } from "components/Forms/SupplementalLepcSection";
import { LepcPicker } from "components/LepcPicker";
import { PrimaryAgencyPicker } from "components/LocalAgencies/PrimaryAgencyPicker";
import { NaicsPicker } from "components/NaicsPicker";
import { SubSection } from "components/SubSection";
import { UnfinishedChangesPrompt } from "components/UnfinishedChangesPrompt";
import { USER_FACING_ERROR_CODE } from "encamp-shared/src/constants/graphqlErrorCodes";
import { BILLING_CONTACT_PERMISSION_ERROR_CODE } from "encamp-shared/src/constants/permissionsTypes";
import { BILLING_CONTACT_STATES, TIER_II_MANAGER_STATES } from "encamp-shared/src/constants/states";
import { gql } from "generated-graphql";
import { ProgramArea, TierIiReportStep } from "generated-graphql/graphql";
import { transform } from "hooks/transform/transformFacility";
import { useCanEditFacilityBillingContact } from "hooks/useAuthorization";
import { useValidatingForm, useValidatingFormContext } from "hooks/useValidatingForm";
import { useCallback, useEffect, useMemo } from "react";
import { Controller, FormProvider, useWatch } from "react-hook-form";
import { CupaAnniversaryDateField } from "routes/Customer/Facility/CupaAnniversaryDate";
import { Billing, FacilityDetailsInput } from "routes/Customer/Facility/FacilityProfile";
import { reportStepMetadata } from "util/reportStepMetadata";
import { getMessageForErrorCodes } from "utils/errorUtils";

import { ReportFloatingBar } from "./ReportFloatingBar";
import { StepperPageHeader } from "./StepperPageHeader";
import { useNavigateReportSaveCancel } from "./useNavigateReportSaveCancel";
import {
  regulatoryFields,
  useFacilityInfoIssues,
  useReport,
  useUpdateFacilityReport,
} from "./useReport";
import { useReportIsVerified } from "./useReportIsVerified";
import { useTouchReportMutation } from "./useTouchReportMutation";
import { useFacilityInputValidation } from "./validationHooks/useFacilityInputValidation";

gql(`
  fragment facilityInfo on Facility {
    id
    name
    companyName
    department
    isMailingAddressSameAsFacility
    isMailingAddressEncamp
    useEncampBillingContact
    isUnmanned
    maxNumOccupants
    fireDepartmentId
    fireDepartment {
      ...FireDepartmentPicker
    }
    supplementalFireDepartments {
      id
      fireDepartment {
        ...FireDepartmentPicker
      }
    }
    lepcId
    lepc {
      ...LepcPicker
    }
    supplementalLepcs {
      id
      lepc {
        ...LepcPicker
      }
    }
    cupaAnniversaryDate
    dbNumber
    naicsCode
    longitude
    latitude
    state
    county
    municipality
    phone
    phone24Hour
    streetAddress1
    streetAddress2
    city
    state
    zip
    country
    mailingCompanyName
    mailingStreetAddress1
    mailingStreetAddress2
    mailingCity
    mailingState
    mailingZip
    mailingCountry
    dateClosed
    dateOpened
    dateInactive
    status
    useEncampBillingContact
  }
`);

export const Facility: React.FC = () => {
  const alerts = useAlerts();
  const { data, loading } = useReport();
  const isVerified = useReportIsVerified();
  const navigateToOverview = useNavigateReportSaveCancel();

  const defaultValues = useMemo(
    () => ({
      name: data?.tierIIReport.facility.name ?? "",
      companyName: data?.tierIIReport.facility.companyName ?? null,
      department: data?.tierIIReport.facility.department ?? null,
      isMailingAddressSameAsFacility: data?.tierIIReport.facility.isMailingAddressEncamp
        ? "encamp"
        : (data?.tierIIReport.facility.isMailingAddressSameAsFacility ?? null),
      isUnmanned: data?.tierIIReport.facility.isUnmanned ?? null,
      maxNumOccupants: data?.tierIIReport.facility.maxNumOccupants ?? null,
      fireDepartmentId: data?.tierIIReport.facility.fireDepartment?.id ?? null,
      lepcId: data?.tierIIReport.facility.lepc?.id ?? null,
      supplementalFireDepartmentIds:
        data?.tierIIReport.facility.supplementalFireDepartments?.map(
          (sfd) => sfd.fireDepartment.id
        ) ?? [],
      supplementalLepcIds:
        data?.tierIIReport.facility.supplementalLepcs?.map((sl) => sl.lepc.id) ?? [],
      dbNumber: data?.tierIIReport.facility.dbNumber ?? null,
      naicsCode: data?.tierIIReport.facility.naicsCode ?? null,
      longitude: data?.tierIIReport.facility.longitude ?? null,
      latitude: data?.tierIIReport.facility.latitude ?? null,
      phone: data?.tierIIReport.facility.phone ?? null,
      phone24Hour: data?.tierIIReport.facility.phone24Hour ?? null,
      county: data?.tierIIReport.facility.county ?? null,
      municipality: data?.tierIIReport.facility.municipality ?? null,

      streetAddress1: data?.tierIIReport.facility.streetAddress1 ?? null,
      streetAddress2: data?.tierIIReport.facility.streetAddress2 ?? null,
      city: data?.tierIIReport.facility.city ?? null,
      state: data?.tierIIReport.facility.state ?? null,
      zip: data?.tierIIReport.facility.zip ?? null,
      country: data?.tierIIReport.facility.country ?? null,
      mailingCompanyName: data?.tierIIReport.facility.mailingCompanyName ?? null,
      mailingStreetAddress1: data?.tierIIReport.facility.mailingStreetAddress1 ?? null,
      mailingStreetAddress2: data?.tierIIReport.facility.mailingStreetAddress2 ?? null,
      mailingCity: data?.tierIIReport.facility.mailingCity ?? null,
      mailingState: data?.tierIIReport.facility.mailingState ?? null,
      mailingZip: data?.tierIIReport.facility.mailingZip ?? null,
      mailingCountry: data?.tierIIReport.facility.mailingCountry ?? null,
      useEncampBillingContact: data?.tierIIReport.facility.useEncampBillingContact ?? null,
      cupaAnniversaryDate: data?.tierIIReport.facility.cupaAnniversaryDate ?? null,
    }),
    [data?.tierIIReport]
  );

  const form = useValidatingForm<FacilityDetailsInput>(
    defaultValues,
    useFacilityInfoIssues(),
    useFacilityInputValidation({
      facilityId: data?.tierIIReport.facility.id ?? "",
      reportId: data?.tierIIReport.id,
      omit: [
        "facilityDocuments",
        "documents",
        "contacts",
        "stateFields",
        "undergroundStorageTanks",
        ...regulatoryFields,
      ],
    }),
    undefined,
    {
      isDisplayOnly: isVerified,
    }
  );

  const {
    handleSubmit,
    formState: { isDirty, isSubmitting, isSubmitted },
  } = form;
  const hasPendingChanges = isDirty && !isSubmitting && !isSubmitted;

  const [updateFacility, { loading: submitting }] = useUpdateFacilityReport();
  const { handleSave: handleTouch } = useTouchReportMutation(
    data?.tierIIReport.id ?? "",
    data?.tierIIReport.touchedSteps ?? [],
    TierIiReportStep.Facility
  );

  const onSubmit = useCallback(
    async (saveData: FacilityDetailsInput) => {
      if (!data) return;

      const input = transform(saveData);

      try {
        await updateFacility({
          variables: {
            id: data.tierIIReport.facility.id,
            facility: input,
            reportId: data.tierIIReport.id,
            programArea: ProgramArea.Epcra,
          },
          awaitRefetchQueries: true,
        });
        await handleTouch();
      } catch (err) {
        // Surface the server's own wording when it wrote one for this user
        // (e.g. the billing-contact permission refusal names the cause).
        alerts.error(
          getMessageForErrorCodes(err, [
            BILLING_CONTACT_PERMISSION_ERROR_CODE,
            USER_FACING_ERROR_CODE,
          ]) ?? "Error saving facility information."
        );
        return;
      }
      alerts.success("Facility information saved successfully.");
    },
    [alerts, data, updateFacility, handleTouch]
  );

  return (
    <FormProvider {...form}>
      <form>
        <Stack>
          <StepperPageHeader
            header={reportStepMetadata.FACILITY.title}
            description={reportStepMetadata.FACILITY.getOverviewDescription()}
          />

          {loading ? (
            <Skelly />
          ) : (
            <>
              <BasicInfo />
              <FacilityAddress />
              <FacilityMailingAddress />
              <FacilityBilling />
              <AdditionalInfo />
              <LocalAgencies />
            </>
          )}

          {!isVerified && (
            <>
              {" "}
              <ReportFloatingBar
                saving={submitting}
                onSaveClick={handleSubmit(onSubmit)}
                loading={loading}
                onCancel={navigateToOverview}
                issues={form.issues}
              />
              <UnfinishedChangesPrompt when={hasPendingChanges} />
            </>
          )}
        </Stack>
      </form>
    </FormProvider>
  );
};

function Skelly() {
  const theme = useTheme();
  return (
    <Stack gap={theme.spacing(2)}>
      <Skeleton variant="rectangular" />
      <Skeleton variant="rectangular" />
      <Skeleton variant="rectangular" />

      <Skeleton variant="rectangular" />
      <Skeleton variant="rectangular" />

      <Grid container spacing={2}>
        <Grid item xs={4}>
          <Skeleton variant="rectangular" />
        </Grid>
        <Grid item xs={4}>
          <Skeleton variant="rectangular" />
        </Grid>
        <Grid item xs={4}>
          <Skeleton variant="rectangular" />
        </Grid>
      </Grid>

      <Skeleton variant="rectangular" />
      <Skeleton variant="rectangular" />

      <Grid container spacing={2}>
        <Grid item xs={6}>
          <Skeleton variant="rectangular" />
          <Skeleton variant="rectangular" />
        </Grid>
        <Grid item xs={6}>
          <Skeleton variant="rectangular" />
          <Skeleton variant="rectangular" />
        </Grid>
      </Grid>
    </Stack>
  );
}

function BasicInfo() {
  const theme = useTheme();
  const { control, metadata } = useValidatingFormContext();
  return (
    <Box marginTop={theme.spacing(1)}>
      <Grid
        container
        columnSpacing={theme.spacing(3)}
        rowSpacing={theme.spacing(metadata?.isDisplayOnly ? 3 : 1)}
      >
        <Grid item xs={12} lg={8}>
          <FormTextField name="name" label="Facility Name" control={control} />
        </Grid>
        <Grid item xs={0} lg={4} />

        <Grid item xs={12} lg={4}>
          <FormTextField name="companyName" label="Company Name" control={control} />
        </Grid>
        <Grid item xs={12} lg={4}>
          <FormTextField name="department" label="Department" control={control} />
        </Grid>
        <Grid item xs={0} lg={4} />

        <Grid item xs={12} lg={4}>
          <FormTextField name="phone" label="Phone Number" control={control} />
        </Grid>
        <Grid item xs={12} lg={4}>
          <FormTextField name="phone24Hour" label="24-hour Phone Number" control={control} />
        </Grid>
        <Grid item xs={0} lg={4} />
      </Grid>
    </Box>
  );
}

function FacilityAddress() {
  const theme = useTheme();
  const { metadata } = useValidatingFormContext();
  return (
    <Box marginTop={theme.spacing(metadata?.isDisplayOnly ? 7 : 3)}>
      <Typography variant="h5">Facility Address</Typography>
      <PhysicalAddress />
    </Box>
  );
}

function FacilityMailingAddress() {
  const theme = useTheme();
  const { metadata } = useValidatingFormContext();
  return (
    <Box marginTop={theme.spacing(metadata?.isDisplayOnly ? 7 : 3)}>
      <Typography variant="h5">Mailing Address</Typography>
      <MailingAddress />
    </Box>
  );
}

function FacilityBilling() {
  const theme = useTheme();
  const { data, loading } = useReport({ fetchPolicy: "no-cache" });
  const canEditBillingContact = useCanEditFacilityBillingContact(data?.tierIIReport.facility.id);
  return (
    <>
      {!loading &&
        data?.tierIIReport.facility.state &&
        BILLING_CONTACT_STATES.includes(data.tierIIReport.facility.state) && (
          <Box marginTop={theme.spacing(3)}>
            <Typography variant="h5">Billing</Typography>
            <Billing readOnly={!canEditBillingContact} />
          </Box>
        )}
    </>
  );
}

function AdditionalInfo() {
  const theme = useTheme();
  const { control, metadata, setValue, watch } = useValidatingFormContext();

  const isUnmanned = watch("isUnmanned");

  useEffect(() => {
    if (isUnmanned) {
      setValue("maxNumOccupants", 0);
    }
  }, [isUnmanned, setValue]);

  return (
    <Box marginTop={theme.spacing(metadata?.isDisplayOnly ? 7 : 3)}>
      <Typography variant="h5">Additional Info</Typography>
      <Grid
        container
        columnSpacing={theme.spacing(3)}
        rowSpacing={theme.spacing(metadata?.isDisplayOnly ? 3 : 1)}
        marginTop={theme.spacing(metadata?.isDisplayOnly ? 0 : 2)}
      >
        <Grid item xs={4}>
          <RadioGroupField
            label="Is this an unmanned facility?"
            name="isUnmanned"
            control={control}
            radioOptions={[
              {
                label: "Yes",
                value: true,
              },
              {
                label: "No",
                value: false,
              },
            ]}
          />
        </Grid>
        <Grid item xs={4}>
          <FormTextField
            name="maxNumOccupants"
            label="Max Occupancy"
            intOnly
            disabled={isUnmanned}
            control={control}
          />
        </Grid>
        <Grid item xs={4} />

        <Grid item xs={12} lg={4}>
          <FormTextField name="dbNumber" label="Dun & Bradstreet Number" control={control} />
        </Grid>

        <Grid item xs={12} lg={8}>
          <Controller
            name="naicsCode"
            control={control}
            render={({ field, fieldState }) => <NaicsPicker {...field} {...fieldState} />}
          />
        </Grid>
      </Grid>
    </Box>
  );
}

function LocalAgencies() {
  const theme = useTheme();
  const { data } = useReport();
  const { control, metadata } = useValidatingFormContext();

  const [state, county] = useWatch({
    control,
    name: ["state", "county"],
  });

  const formattedCounty = county?.replace(/\s+County$/, "");

  // Determine if the facility is in California
  const isCA = state === "CA";

  // Dynamic title for LEPC/CUPA section
  const lepcTitle = isCA
    ? "Certified Unified Program Agency (CUPA)"
    : "Local Emergency Planning Committee (LEPC)";

  return (
    <Box marginTop={theme.spacing(metadata?.isDisplayOnly ? 7 : 3)}>
      <Typography variant="h5">Local Agencies</Typography>
      <SubSection
        title={lepcTitle}
        style={{
          marginLeft: 0,
        }}
        tooltip={
          TIER_II_MANAGER_STATES.includes(state) && county
            ? `Available selections are restricted to those in ${formattedCounty} County`
            : undefined
        }
      >
        <PrimaryAgencyPicker
          name="lepcId"
          control={control}
          facilityState={state}
          facilityCounty={county}
          facilityLatitude={data?.tierIIReport.facility.latitude}
          facilityLongitude={data?.tierIIReport.facility.longitude}
          disabled={metadata?.isDisplayOnly}
          helperText="Primary: this agency will be included in your report in the portal."
          PickerComponent={LepcPicker}
          existingAgency={data?.tierIIReport.facility.lepc}
          pickerDescription="Choose from the list of LEPCs in your state. If an LEPC does not have a latitude/longitude on file or if it has a P.O. Box, it will appear at the top of this list without a distance. If you need to report to multiple LEPCs, you can add more below."
        />
        <SupplementalLepcSection
          disabled={!data?.tierIIReport.facility.lepc}
          readOnly={metadata?.isDisplayOnly}
          facilityState={state}
          existingAgencies={data?.tierIIReport.facility.supplementalLepcs}
          primaryLepcId={data?.tierIIReport.facility.lepc?.id}
          facilityLatitude={data?.tierIIReport.facility.latitude}
          facilityLongitude={data?.tierIIReport.facility.longitude}
        />
        {isCA && (
          <CupaAnniversaryDateField name="cupaAnniversaryDate" control={control} sx={{ pt: 2 }} />
        )}
      </SubSection>
      {!isCA && (
        <SubSection
          title="Fire District"
          style={{
            marginLeft: 0,
            marginTop: theme.spacing(metadata?.isDisplayOnly ? 5 : 3),
          }}
          tooltip={
            TIER_II_MANAGER_STATES.includes(state) && county
              ? `Available selections are restricted to those in ${formattedCounty} County`
              : undefined
          }
        >
          <PrimaryAgencyPicker
            name="fireDepartmentId"
            control={control}
            facilityState={state}
            facilityCounty={county}
            facilityLatitude={data?.tierIIReport.facility.latitude}
            facilityLongitude={data?.tierIIReport.facility.longitude}
            disabled={metadata?.isDisplayOnly}
            helperText="Primary: this agency will be included in your report in the portal."
            PickerComponent={FireDepartmentPicker}
            existingAgency={data?.tierIIReport.facility.fireDepartment}
            pickerDescription="Choose from the list of Fire Districts. Be sure to select the correct FD for your facility, which may not be the closest agency. If an FD does not have a latitude/longitude on file or if it has a P.O. Box, it will appear at the top of this list without a distance. If you need to report to multiple FDs, you can add more below."
          />
          <SupplementalFireDepartmentSection
            disabled={!data?.tierIIReport.facility.fireDepartment}
            readOnly={metadata?.isDisplayOnly}
            facilityState={state}
            existingAgencies={data?.tierIIReport.facility.supplementalFireDepartments}
            primaryFireDepartmentId={data?.tierIIReport.facility.fireDepartment?.id}
            facilityLatitude={data?.tierIIReport.facility.latitude}
            facilityLongitude={data?.tierIIReport.facility.longitude}
          />
        </SubSection>
      )}
    </Box>
  );
}
